import {
  ChangeEvent,
  useState,
} from 'react';
import { useStateVar } from 'helpers/useStateVar';
import { Flex } from 'reflexbox';
import {
  TextLine, CustomSelect,
} from './styles';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import {
  Input, Checkbox, Button, InputCalculator,
} from 'components';
import { ToggleSwitchMini } from '../ToggleSwitch';
import { ComponentProps, ExceptionDut4 } from './types';
import { ModoSobDemanda, ModoSobDemandaData } from '../ScheduleEditCardDut4/components/modoSobDemanda';
import { ModoEco, ModoEcoData } from '../ScheduleEditCardDut4/components/modoEco';
import {
  parseScheduleData, validateSchedule, setpointFromActionCmd, actionPostModeFromCmd,
} from '../ScheduleEditCardDut4/constants';
import { toast } from 'react-toastify';
import { colors } from '~/styles/colors';

const simpleActionModesOptions = [
  { label: t('refrigerar'), value: '1_REFRIGERAR' },
  { label: t('desligar'), value: '0_DESLIGAR' },
  { label: t('ventilar'), value: '2_VENTILAR' },
];

const advancedActionModesOptions = [
  { label: t('modoSobDemanda'), value: '3_SOB_DEMANDA' },
  { label: t('modoEco'), value: '4_ECO' },
];

const DEFAULT_SETPOINT_VALUES = [16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30];

const advancedModes = ['3_SOB_DEMANDA', '4_ECO'];

export const ExceptionEditCardDut4 = (props: ComponentProps): JSX.Element => {
  const { t } = useTranslation();

  const [state, render, setState] = useStateVar({
    formData: {
      DUT_EXCEPTION_ID: props.exception?.DUT_EXCEPTION_ID ?? null,
      EXCEPTION_TITLE: props.exception?.EXCEPTION_TITLE ?? null,
      REPEAT_YEARLY: props.exception != null ? props.exception.REPEAT_YEARLY : false,
      EXCEPTION_DATE: (() => {
        const d = props.exception?.EXCEPTION_DATE;
        if (!d) return null;
        // converte YYYY-MM-DD → DD/MM/YYYY para o input com máscara
        return /^\d{4}-\d{2}-\d{2}$/.test(d)
          ? `${d.substring(8, 10)}/${d.substring(5, 7)}/${d.substring(0, 4)}`
          : d;
      })(),
      BEGIN_TIME: props.exception?.BEGIN_TIME ?? null,
      END_TIME: props.exception?.END_TIME ?? null,
      EXCEPTION_STATUS_ID: props.exception?.EXCEPTION_STATUS_ID ?? 0,
      CTRLOPER: props.exception?.CTRLOPER ?? null,
      SETPOINT: props.exception?.SETPOINT?.toString() ?? '22',
    },
    allDay: props.exception
      ? props.exception.BEGIN_TIME === '00:00' && props.exception.END_TIME === '23:59'
      : false as boolean,
  });

  const [modeData, setModeData] = useState({
    type: '' as 'eco' | 'modoSobDemanda',
    sobDemanda: {} as ModoSobDemandaData,
    eco: {} as ModoEcoData,
  });

  const [modoAvancado, setModoAvancado] = useState(
    advancedModes.includes(props.exception?.CTRLOPER ?? ''),
  );

  const actionModesOptions = modoAvancado ? advancedActionModesOptions : simpleActionModesOptions;

  function setFormData(obj, withRender = true) {
    Object.assign(state.formData, obj);
    if (obj.hasOwnProperty('BEGIN_TIME') || obj.hasOwnProperty('END_TIME')) {
      state.allDay = state.formData.BEGIN_TIME === '00:00' && state.formData.END_TIME === '23:59';
    }
    if (withRender) render();
  }

  function handleTimeChange(field: 'BEGIN_TIME' | 'END_TIME', value: string): void {
    if (!/^\d{2}:\d{2}$/.test(value)) {
      setFormData({ [field]: value });
      return;
    }

    // eslint-disable-next-line prefer-const
    let [hours, minutes] = value.split(':').map(Number);
    if (hours > 23) hours = 23;
    let formattedValue = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    if (field === 'END_TIME' && formattedValue === '00:00') {
      formattedValue = '23:59';
    }
    setFormData({ [field]: formattedValue });
  }

  function increaseDecrease(value: number) {
    state.formData.SETPOINT = String(value);
    render();
  }

  function saveException() {
    const timeRegex = /^\d{2}:\d{2}$/;

    if (!state.formData.EXCEPTION_TITLE?.trim()) {
      toast.error(t('erroTituloProgramacaoNecessario'));
      return;
    }

    if (!state.formData.EXCEPTION_DATE || state.formData.EXCEPTION_DATE.length !== 10 || state.formData.EXCEPTION_DATE.includes('_')) {
      toast.error(t('erroDataExcecaoObrigatoria'));
      return;
    }

    if (!timeRegex.test(state.formData.BEGIN_TIME ?? '')) {
      toast.error(t('erroHorarioInicioObrigatorio'));
      return;
    }

    if (!timeRegex.test(state.formData.END_TIME ?? '')) {
      toast.error(t('erroHorarioFimObrigatorio'));
      return;
    }

    if ((state.formData.BEGIN_TIME ?? '') >= (state.formData.END_TIME ?? '')) {
      toast.error(t('erroHorarioInicioMenorHorarioFim'));
      return;
    }

    if (!state.formData.CTRLOPER) {
      toast.error(t('erroNecessarioModoOperacao'));
      return;
    }

    const modesRequiringIr = ['1_REFRIGERAR', '3_SOB_DEMANDA', '4_ECO'];
    if (!props.isMultipleDevProg && props.irCommands.length === 0 && modesRequiringIr.includes(state.formData.CTRLOPER)) {
      toast.error(t('erroModoPrecisaComandoIr'));
      return;
    }

    if (!props.isMultipleDevProg && state.formData.CTRLOPER === '3_SOB_DEMANDA' && props.irCommands.length > 0) {
      const availableTemps = props.irCommands.map((cmd) => cmd.TEMPER);
      const sobDemanda = modeData.sobDemanda;

      if (sobDemanda.actionMode === 'COOL' && (!sobDemanda.setpoint || !availableTemps.includes(Number(sobDemanda.setpoint)))) {
        toast.error(t('erroModoPrecisaComandoIr'));
        return;
      }

      if (sobDemanda.actionPostMode === 'COOL' && (!sobDemanda.actionPostSetpoint || !availableTemps.includes(Number(sobDemanda.actionPostSetpoint)))) {
        toast.error(t('erroModoPrecisaComandoIr'));
        return;
      }

      if ((sobDemanda.lti_cmd === 'FAN' || sobDemanda.lti_cmd === 'OFF') && sobDemanda.lti_degree && !availableTemps.includes(Number(sobDemanda.lti_degree))) {
        toast.error(t('erroModoPrecisaComandoIr'));
        return;
      }
    }

    if (!props.isMultipleDevProg && state.formData.CTRLOPER === '4_ECO' && props.irCommands.length > 0) {
      const availableTemps = props.irCommands.map((cmd) => cmd.TEMPER);
      const eco = modeData.eco;

      if (!eco.setpoint || !availableTemps.includes(Number(eco.setpoint))) {
        toast.error(t('erroModoPrecisaComandoIr'));
        return;
      }

      if ((eco.lti_cmd === 'FAN' || eco.lti_cmd === 'OFF') && eco.lti_degree && !availableTemps.includes(Number(eco.lti_degree))) {
        toast.error(t('erroModoPrecisaComandoIr'));
        return;
      }
    }

    const ctrloper = state.formData.CTRLOPER;
    let effectiveType: 'modoSobDemanda' | 'eco' | '' = '';
    if (ctrloper === '3_SOB_DEMANDA') effectiveType = 'modoSobDemanda';
    else if (ctrloper === '4_ECO') effectiveType = 'eco';

    const error = validateSchedule(
      effectiveType,
      effectiveType === 'eco' ? modeData.eco : modeData.sobDemanda,
    );
    if (error) {
      toast.error(error);
      return;
    }

    const scheduleData = parseScheduleData(
      state.formData.EXCEPTION_TITLE,
      true,
      ctrloper,
      {
        mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false,
      },
      state.formData.BEGIN_TIME ?? '',
      state.formData.END_TIME ?? '',
      modeData.type === 'eco' ? modeData.eco : modeData.sobDemanda,
      state.formData.SETPOINT,
      state.formData.DUT_EXCEPTION_ID,
    );

    const rawDate = state.formData.EXCEPTION_DATE ?? '';
    const isoDate = rawDate.length === 10
      ? `${rawDate.substring(6, 10)}-${rawDate.substring(3, 5)}-${rawDate.substring(0, 2)}`
      : rawDate;

    const exception: ExceptionDut4 = {
      DUT_EXCEPTION_ID: state.formData.DUT_EXCEPTION_ID ?? undefined,
      EXCEPTION_TITLE: scheduleData.SCHEDULE_TITLE,
      REPEAT_YEARLY: state.formData.REPEAT_YEARLY,
      EXCEPTION_DATE: isoDate,
      BEGIN_TIME: scheduleData.BEGIN_TIME,
      END_TIME: scheduleData.END_TIME,
      EXCEPTION_STATUS_ID: 1,
      CTRLOPER: ctrloper,
      SETPOINT: scheduleData.SETPOINT,
      TEMP_TRIGGER: scheduleData.TEMP_TRIGGER,
      SP_CMD: scheduleData.SP_CMD,
      SP_END_CMD: scheduleData.SP_END_CMD,
      SP_ACTION: scheduleData.SP_ACTION,
      SP_INTV: scheduleData.SP_INTV,
      UPPER_HYSTERESIS: scheduleData.UPPER_HYSTERESIS,
      LOWER_HYSTERESIS: scheduleData.LOWER_HYSTERESIS,
      LTC: scheduleData.LTC,
      LTI_CMD: scheduleData.LTI_CMD,
      LTI: scheduleData.LTI,
      DEFAULT_COMMAND: scheduleData.DEFAULT_COMMAND,
    };

    props.onHandleSave(exception, props.cardIndex);
    props.onHandleCancel();
  }

  return (
    <Flex
      flexWrap="nowrap"
      flexDirection="column"
      width="452px"
      style={{
        borderTop: `15px solid ${colors.brand.principal}`,
        borderRadius: '5px',
        marginBottom: '20px',
      }}
    >
      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{ marginTop: '40px', marginLeft: '27px' }}
      >
        <div style={{ fontSize: '15px', fontWeight: 'bold' }}>
          {t('adicionarExcecao')}
        </div>
      </Flex>

      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{ marginTop: '13px', marginLeft: '27px' }}
      >
        <div style={{ width: '145px', fontSize: '12px', fontWeight: 'bold' }}>
          {t('titulo')}
        </div>
      </Flex>
      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{ marginTop: '7px', marginLeft: '27px' }}
      >
        <TextLine style={{ width: '374px' }}>
          <Input
            data-testid="exception-dut4-title-input"
            type="text"
            value={state.formData.EXCEPTION_TITLE ?? undefined}
            label=""
            style={{ width: '374px' }}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData({ EXCEPTION_TITLE: e.target.value })}
          />
        </TextLine>
      </Flex>

      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{ marginTop: '13px', marginLeft: '27px' }}
      >
        <div style={{ width: '145px', fontSize: '12px', fontWeight: 'bold' }}>
          {t('selecioneDia')}
        </div>
      </Flex>
      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{ marginTop: '7px', marginLeft: '27px' }}
      >
        <TextLine style={{ width: '199px' }}>
          <Input
            data-testid="exception-dut4-date-input"
            style={{ width: '199px' }}
            value={state.formData.EXCEPTION_DATE ?? undefined}
            label=""
            mask={[/[0-3]/, /\d/, '/', /[0-1]/, /\d/, '/', /2/, /0/, /\d/, /\d/]}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData({ EXCEPTION_DATE: e.target.value })}
          />
        </TextLine>
        <div style={{ marginLeft: '27px', marginTop: '13px' }}>
          <label onClick={() => setFormData({ REPEAT_YEARLY: !state.formData.REPEAT_YEARLY })}>
            <Checkbox data-testid="exception-dut4-repeat-yearly-checkbox" checked={state.formData.REPEAT_YEARLY} />
          </label>
        </div>
        <div style={{
          fontSize: '12px', fontWeight: 'bold', marginTop: '15px', marginLeft: '8px',
        }}
        >
          {t('repetirTodoOAno')}
        </div>
      </Flex>

      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{ marginTop: '13px', marginLeft: '27px' }}
      >
        <div style={{ fontSize: '12px', fontWeight: 'bold' }}>{t('horarioInicio')}</div>
        <div style={{ fontSize: '12px', fontWeight: 'bold', marginLeft: '60px' }}>{t('horarioFim')}</div>
      </Flex>
      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{ marginTop: '7px', marginLeft: '27px' }}
      >
        <TextLine style={{ width: '112px' }}>
          <Input
            data-testid="exception-dut4-begin-time-input"
            value={state.formData.BEGIN_TIME ?? undefined}
            mask={[/[0-2]/, /\d/, ':', /[0-5]/, /\d/]}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleTimeChange('BEGIN_TIME', e.target.value)}
          />
        </TextLine>
        <TextLine style={{ width: '112px', marginLeft: '34px' }}>
          <Input
            data-testid="exception-dut4-end-time-input"
            value={state.formData.END_TIME ?? undefined}
            mask={[/[0-2]/, /\d/, ':', /[0-5]/, /\d/]}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleTimeChange('END_TIME', e.target.value)}
          />
        </TextLine>
        <div style={{ marginLeft: '20px', marginTop: '14px' }}>
          <label
            onClick={() => {
              state.allDay = !state.allDay;
              if (state.allDay) {
                setFormData({ BEGIN_TIME: '00:00', END_TIME: '23:59' });
              } else {
                setFormData({ BEGIN_TIME: null, END_TIME: null });
              }
            }}
          >
            <Checkbox data-testid="exception-dut4-all-day-checkbox" checked={state.allDay} />
          </label>
        </div>
        <div style={{
          fontSize: '12px', fontWeight: 'bold', marginTop: '16px', marginLeft: '8px',
        }}
        >
          {t('diaInteiro')}
        </div>
      </Flex>

      <Flex
        flexWrap="nowrap"
        flexDirection="column"
        style={{ marginTop: '15px', marginLeft: '27px', gap: '8px' }}
      >
        <div style={{ fontSize: '12px', fontWeight: 'bold' }}>{t('funcionamento')}</div>
        <Flex
          flexWrap="nowrap"
          flexDirection="row"
          alignItems="center"
          style={{ gap: '8px', width: 'fit-content' }}
        >
          <span style={{
            fontSize: '11px',
            color: modoAvancado ? colors.Grey200 : colors.Blue300,
            fontWeight: modoAvancado ? 'normal' : 'bold',
            transition: 'color 0.2s',
          }}
          >
            {t('modoSimples')}
          </span>
          <ToggleSwitchMini
            dataTestId="exception-dut4-modo-avancado-toggle"
            checked={modoAvancado}
            onClick={(e) => {
              e.preventDefault();
              const next = !modoAvancado;
              setModoAvancado(next);
              if (!next && advancedModes.includes(state.formData.CTRLOPER ?? '')) {
                setState({ formData: { ...state.formData, CTRLOPER: null, SETPOINT: '22' } });
                setModeData({
                  type: '' as 'eco' | 'modoSobDemanda',
                  sobDemanda: {} as ModoSobDemandaData,
                  eco: {} as ModoEcoData,
                });
              }
            }}
          />
          <span style={{
            fontSize: '11px',
            color: modoAvancado ? colors.Blue300 : colors.Grey200,
            fontWeight: modoAvancado ? 'bold' : 'normal',
            transition: 'color 0.2s',
          }}
          >
            {t('modoAvancado')}
          </span>
        </Flex>
      </Flex>

      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{ marginTop: '10px', marginLeft: '24px' }}
      >
        <CustomSelect
          data-testid="exception-dut4-action-mode-select"
          options={actionModesOptions}
          value={actionModesOptions.find((option) => option.value === state.formData.CTRLOPER)?.label || ''}
          placeholder={t('modoDeOperacao')}
          onSelect={(item) => {
            const modesWithoutFields = ['0_DESLIGAR', '2_VENTILAR'];
            const clearFields = modesWithoutFields.includes(item.value);
            let setpoint: string | number = state.formData.SETPOINT;
            if (clearFields) setpoint = '22';
            else if (item.value === '1_REFRIGERAR') setpoint = props.irCommands?.[0]?.TEMPER ?? state.formData.SETPOINT;
            if (clearFields) {
              setModeData({
                type: '' as 'eco' | 'modoSobDemanda',
                sobDemanda: {} as ModoSobDemandaData,
                eco: {} as ModoEcoData,
              });
            }
            setState({
              formData: {
                ...state.formData,
                CTRLOPER: item.value,
                SETPOINT: setpoint != null ? String(setpoint) : '22',
              },
            });
          }}
          style={state.formData.CTRLOPER === '1_REFRIGERAR' ? { width: '188px' } : { width: '400px' }}
          notNull
        />
        {state.formData.CTRLOPER === '1_REFRIGERAR' && (
          <InputCalculator
            data-testid="exception-dut4-setpoint-input"
            label={t('setpointC')}
            value={state.formData.SETPOINT ? state.formData.SETPOINT.toString() : ''}
            possibleValues={props.isMultipleDevProg ? DEFAULT_SETPOINT_VALUES : props.irCommands.map((command) => command.TEMPER)}
            unity="ºC"
            onIncreaseDecrease={increaseDecrease}
            isSetpoint
            disabled={!props.isMultipleDevProg && props.irCommands.length === 0}
            style={{ height: '50px', marginRight: '24px' }}
          />
        )}
      </Flex>

      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{ marginTop: '10px', marginLeft: '24px', marginRight: '24px' }}
      >
        {state.formData.CTRLOPER === '3_SOB_DEMANDA' && (
          <ModoSobDemanda
            irCommands={props.irCommands}
            isMultipleDevProg={props.isMultipleDevProg}
            onChange={(data) => setModeData((prev) => ({ ...prev, sobDemanda: data, type: 'modoSobDemanda' }))}
            initialData={props.action === 'edit' && props.exception?.CTRLOPER === '3_SOB_DEMANDA' ? {
              driveTemperature: props.exception.TEMP_TRIGGER != null ? String(props.exception.TEMP_TRIGGER) : '',
              actionMode: props.exception.SP_ACTION ?? null,
              setpoint: setpointFromActionCmd(props.exception.SP_CMD),
              actionTime: props.exception.SP_INTV?.toString() ?? '5',
              actionPostMode: actionPostModeFromCmd(props.exception.SP_END_CMD),
              actionPostSetpoint: setpointFromActionCmd(props.exception.SP_END_CMD),
              lti_cmd: props.exception.LTI_CMD ?? 'NO_ACTION',
              lti_degree: props.exception.LTI != null ? String(props.exception.LTI) : '',
              upperHysteresis: props.exception.UPPER_HYSTERESIS != null ? String(props.exception.UPPER_HYSTERESIS) : '',
              lowerHysteresis: props.exception.LOWER_HYSTERESIS != null ? String(props.exception.LOWER_HYSTERESIS) : '',
            } : undefined}
          />
        )}
        {state.formData.CTRLOPER === '4_ECO' && (
          <ModoEco
            irCommands={props.irCommands}
            isMultipleDevProg={props.isMultipleDevProg}
            onChange={(data) => setModeData((prev) => ({ ...prev, eco: data, type: 'eco' }))}
            initialData={props.action === 'edit' && props.exception?.CTRLOPER === '4_ECO' ? {
              setpoint: props.exception.SETPOINT != null ? String(props.exception.SETPOINT) : '',
              ltc_degree: props.exception.LTC != null ? String(props.exception.LTC) : '',
              lti_degree: props.exception.LTI != null ? String(props.exception.LTI) : '',
              lti_cmd: props.exception.LTI_CMD ?? '',
              upperHysteresis: props.exception.UPPER_HYSTERESIS != null ? String(props.exception.UPPER_HYSTERESIS) : '',
              lowerHysteresis: props.exception.LOWER_HYSTERESIS != null ? String(props.exception.LOWER_HYSTERESIS) : '',
              initialBehaviorProgrammingHour: props.exception.DEFAULT_COMMAND ?? '',
            } : undefined}
          />
        )}
      </Flex>

      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{ marginTop: '41px', marginLeft: '138px' }}
      >
        <Button
          data-testid="exception-dut4-save-btn"
          style={{ maxWidth: '178px' }}
          onClick={() => saveException()}
          variant="primary"
        >
          {`${props.cardIndex == null ? t('botaoAdicionar') : t('botaoEditar')}`}
        </Button>
      </Flex>
      <Flex
        flexWrap="nowrap"
        flexDirection="row"
        style={{ marginTop: '22px', marginLeft: '195px' }}
      >
        <button
          data-testid="exception-dut4-cancel-btn"
          type="button"
          style={{
            color: colors.DarkGrey,
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
            textDecoration: 'underline',
            font: 'inherit',
          }}
          onClick={(e) => { e.stopPropagation(); props.onHandleCancel(); }}
        >
          {t('botaoCancelar')}
        </button>
      </Flex>
    </Flex>
  );
};
